import React, { useMemo, useState } from 'react';
import moment from 'moment';

import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Modal, Select } from 'antd';
import { connect } from 'react-redux';

import { ICondoUnitWeek } from '@common-types';
import { CloseSvg } from '@share/assets';

import './style.scss';

const modalWidth = 500;
const ALL_MONTHS = 'all';

type SortKey = 'date_asc' | 'date_desc' | 'price_asc' | 'price_desc' | 'nights_asc' | 'nights_desc';

interface IProps extends RouteComponentProps {
  condo: ICondoUnitWeek;

  visible: boolean;

  onCancel: () => void;
}

function CondoWeeksDatesModalComponent(props: IProps) {
  const { condo, visible, onCancel } = props;
  const { propertyName, dates } = condo;

  const [selectedMonth, setSelectedMonth] = useState<string>(ALL_MONTHS);
  const [sortBy, setSortBy] = useState<SortKey>('date_asc');

  const datesCount = dates?.length || 0;

  const availableMonths = useMemo(() => {
    const months = new Set<string>();
    (dates || []).forEach(d => {
      months.add(moment(d.checkIn, 'yyyy-MM-DD').format('YYYY-MM'));
    });
    return Array.from(months).sort();
  }, [dates]);

  const filteredDates = useMemo(() => {
    if (selectedMonth === ALL_MONTHS) {
      return dates || [];
    }
    return (dates || []).filter(d => moment(d.checkIn, 'yyyy-MM-DD').format('YYYY-MM') === selectedMonth);
  }, [dates, selectedMonth]);

  const sortedDates = useMemo(() => {
    const sorted = [...filteredDates];
    const lastUnderscore = sortBy.lastIndexOf('_');
    const sortKey = sortBy.substring(0, lastUnderscore);
    const sortDir = sortBy.substring(lastUnderscore + 1);
    const factor = sortDir === 'desc' ? -1 : 1;

    if (sortKey === 'date') {
      sorted.sort((a, b) => (a.checkIn || '').localeCompare(b.checkIn || '') * factor);
    } else if (sortKey === 'price') {
      sorted.sort((a, b) => ((a.price || 0) - (b.price || 0)) * factor);
    } else if (sortKey === 'nights') {
      sorted.sort((a, b) => ((a.daysOfStay || 0) - (b.daysOfStay || 0)) * factor);
    }
    return sorted;
  }, [filteredDates, sortBy]);

  const groupedDates = useMemo(() => {
    if (selectedMonth !== ALL_MONTHS) {
      return [{ month: null as string | null, items: sortedDates }];
    }
    const groups: { month: string | null; items: typeof sortedDates }[] = [];
    sortedDates.forEach(d => {
      const m = moment(d.checkIn, 'yyyy-MM-DD').format('YYYY-MM');
      let group = groups.find(g => g.month === m);
      if (!group) {
        group = { month: m, items: [] };
        groups.push(group);
      }
      group.items.push(d);
    });
    return groups;
  }, [sortedDates, selectedMonth]);

  const showMonthsDropdown = availableMonths.length > 1;
  const showSortDropdown = datesCount > 1;
  const showFiltersRow = showMonthsDropdown || showSortDropdown;

  const monthOptions = [
    { value: ALL_MONTHS, label: 'All months' },
    ...availableMonths.map(m => ({ value: m, label: moment(m, 'YYYY-MM').format('MMMM YYYY') })),
  ];

  const sortOptions: { value: SortKey; label: string }[] = [
    { value: 'date_asc', label: 'Date - asc' },
    { value: 'date_desc', label: 'Date - desc' },
    { value: 'price_asc', label: 'Price - asc' },
    { value: 'price_desc', label: 'Price - desc' },
    { value: 'nights_asc', label: 'Nights - asc' },
    { value: 'nights_desc', label: 'Nights - desc' },
  ];

  return (
    <Modal
      className="condo-weeks-dates-modal"
      wrapClassName="condo-weeks-dates-modal__wrap"
      visible={visible}
      footer={null}
      onCancel={onCancel}
      closable={true}
      destroyOnClose={true}
      closeIcon={<CloseSvg />}
      width={modalWidth}
      title={null}
    >
      <div className="condo-weeks-dates-modal__header">
        <div className="condo-weeks-dates-modal__title">{propertyName}</div>
        <div className="condo-weeks-dates-modal__subtitle">{datesCount} dates available</div>
      </div>

      {showFiltersRow ? (
        <div className="condo-weeks-dates-modal__filters">
          {showMonthsDropdown ? (
            <Select
              className="condo-weeks-dates-modal__select"
              value={selectedMonth}
              onChange={(v: string) => setSelectedMonth(v)}
              getPopupContainer={(triggerNode) => triggerNode.parentElement as HTMLElement}
              options={monthOptions.map(o => ({ key: o.value, value: o.value, label: o.label }))}
            />) : null}
          {showSortDropdown ? (
            <Select
              className="condo-weeks-dates-modal__select"
              value={sortBy}
              onChange={(v: SortKey) => setSortBy(v)}
              getPopupContainer={(triggerNode) => triggerNode.parentElement as HTMLElement}
              options={sortOptions.map(o => ({ key: o.value, value: o.value, label: o.label }))}
            />) : null}
        </div>) : null}

      <div className="condo-weeks-dates-modal__table">
        <div className="condo-weeks-dates-modal__table-header">
          <div className="condo-weeks-dates-modal__col-dates">DATES</div>
          <div className="condo-weeks-dates-modal__col-nights">NIGHTS</div>
          <div className="condo-weeks-dates-modal__col-price">PRICE</div>
        </div>

        <div className="condo-weeks-dates-modal__table-body">
          {groupedDates.map((group, gi) => (
            <React.Fragment key={`group_${group.month || 'all'}_${gi}`}>
              {group.month ? (
                <div className="condo-weeks-dates-modal__month-header">
                  {moment(group.month, 'YYYY-MM').format('MMMM YYYY').toUpperCase()}
                </div>) : null}
              {group.items.map((d, i) => {
                const checkIn = moment(d.checkIn, 'yyyy-MM-DD');
                const checkOut = moment(d.checkOut, 'yyyy-MM-DD');
                const price = Math.floor((d.price || 0) * 100) / 100;
                return (
                  <div className="condo-weeks-dates-modal__row" key={`row_${gi}_${i}`}>
                    <div className="condo-weeks-dates-modal__col-dates">
                      <span className="condo-weeks-dates-modal__date-range">{checkIn.format('MMM D')} – {checkOut.format('MMM D')}</span>
                      <span className="condo-weeks-dates-modal__weekday">{checkIn.format('ddd')}</span>
                    </div>
                    <div className="condo-weeks-dates-modal__col-nights">{d.daysOfStay}</div>
                    <div className="condo-weeks-dates-modal__col-price">${price}</div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </Modal>
  );
}

const CondoWeeksDatesModalRouter = withRouter(CondoWeeksDatesModalComponent);

export const CondoWeeksDatesModal = connect()(CondoWeeksDatesModalRouter);
